/**
 * Suppress benign ResizeObserver loop errors that occur with virtualized lists.
 * This error means ResizeObserver couldn't deliver all notifications in one frame,
 * which is harmless but noisy. See:
 * - https://github.com/WICG/resize-observer/issues/38
 * - https://github.com/w3c/csswg-drafts/issues/5023
 * - https://github.com/w3c/csswg-drafts/issues/6173
 * - https://github.com/w3c/csswg-drafts/issues/5488
 *
 * This file must be imported at the very top of index.tsx to ensure the error
 * handlers are registered before webpack's dev server overlay handlers.
 */

const RESIZE_OBSERVER_ERROR_PATTERNS = [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
];

function isResizeObserverError(msg: string | undefined | null): boolean {
    if (!msg) return false;
    return RESIZE_OBSERVER_ERROR_PATTERNS.some((pattern) => msg.includes(pattern));
}

// Suppress via error event (capture phase to intercept before other handlers)
window.addEventListener(
    "error",
    (e) => {
        if (isResizeObserverError(e.message) || isResizeObserverError(e.error?.message)) {
            e.stopImmediatePropagation();
            e.stopPropagation();
            e.preventDefault();
            return true;
        }
    },
    true,
);

// Also suppress via window.onerror for older error paths
const originalOnError = window.onerror;
window.onerror = (message, ...args) => {
    if (typeof message === "string" && isResizeObserverError(message)) {
        return true; // Suppress
    }
    return originalOnError ? originalOnError(message, ...args) : false;
};

// Watch for the webpack dev server overlay iframe and hide it when it only contains
// ResizeObserver errors. This uses a MutationObserver to detect when the overlay is added.
if (process.env.NODE_ENV === "development") {
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node instanceof HTMLIFrameElement) {
                    // Check if this is the webpack dev server overlay
                    const isOverlay =
                        node.id === "webpack-dev-server-client-overlay" ||
                        (node.style.position === "fixed" &&
                            node.style.zIndex === "2147483647" &&
                            node.style.inset === "0px");

                    if (isOverlay) {
                        // Wait for iframe to load, then check its content
                        const checkAndHideOverlay = () => {
                            try {
                                const iframeDoc = node.contentDocument || node.contentWindow?.document;
                                if (iframeDoc) {
                                    const bodyText = iframeDoc.body?.innerText || "";
                                    // If the overlay only contains ResizeObserver errors, hide it
                                    if (isResizeObserverError(bodyText)) {
                                        node.style.display = "none";
                                    }
                                }
                            } catch {
                                // Cross-origin iframe, can't access content
                            }
                        };

                        // Check immediately and after a short delay (for content to load)
                        checkAndHideOverlay();
                        setTimeout(checkAndHideOverlay, 100);
                        setTimeout(checkAndHideOverlay, 500);
                    }
                }
            }
        }
    });

    observer.observe(document.body, {childList: true, subtree: true});
}

export {};
